import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { StrapiConfig } from '../../utils/Setting';

export const usePortfolioData = (mode = 'full') => {
  const { userData } = useSelector((state) => state.auth);
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Get Strapi API token
  const getStrapiToken = useCallback(() => {
    if (!StrapiConfig.apiToken) {
      throw new Error('REACT_STRAPI_API_TOKEN not configured');
    }
    return StrapiConfig.apiToken;
  }, []);

  // Fetch portfolios with optimized query for display (only ordered images)
  const fetchPortfolios = useCallback(async () => {
    try {
      setLoading(true);
      
      const strapiToken = getStrapiToken();
      
      // Use organization ID (contractor_data.id) to find contractor
      const organizationId = userData?.contractor_data?.id || userData?.id;
      
      if (!organizationId) {
        setPortfolios([]);
        setLoading(false);
        return;
      }
      
      // Get the contractor's portfolios directly using external_id (organization ID)
      const cacheBuster = Date.now();
      const portfolioResponse = await fetch(`${StrapiConfig.url}/api/portfolios?filters[contractor][external_id][$eq]=${organizationId}&populate[gallery][populate]=image&sort=createdAt:desc&_t=${cacheBuster}`, {
        headers: { 'Authorization': `Bearer ${strapiToken}` }
      });
      
      if (!portfolioResponse.ok) {
        throw new Error('Failed to fetch portfolios');
      }
      
      const portfolioData = await portfolioResponse.json();
      
      if (portfolioData.error) {
        if (portfolioData.error.status === 404) {
          setPortfolios([]);
        } else {
          throw new Error(portfolioData.error.message);
        }
      } else {
        // Portfolios are now direct array (each portfolio IS a project)
        setPortfolios(portfolioData.data || []);
      }
    } catch (error) {
      console.error('Error fetching portfolios:', error);
      toast.error('Failed to load portfolio data');
    } finally {
      setLoading(false);
    }
  }, [userData.id, getStrapiToken]);

  // Helper function to get ordered images from gallery
  const getOrderedImages = useCallback((project) => {
    if (!project.gallery || !Array.isArray(project.gallery)) {
      // Fallback to old structure
      return (project.image_gallery || []).slice(0, 3);
    }
    
    // Check if gallery items have image data populated
    const hasImageData = project.gallery.some(item => item.image && typeof item.image === 'object');
    
    if (!hasImageData) {
      return [];
    }
    
    // API returns filtered (order > 0) and sorted images, limit to 3 for display
    return project.gallery.map(item => item.image).slice(0, 3);
  }, []);

  // Delete project
  const confirmDelete = useCallback(async () => {
    if (!projectToDelete) return;
    
    try {
      setDeleting(true);
      const strapiToken = getStrapiToken();
      
      const deleteUrl = projectToDelete.documentId 
        ? `${StrapiConfig.url}/api/portfolios/${projectToDelete.documentId}`
        : `${StrapiConfig.url}/api/portfolios/${projectToDelete.id}`;
      
      // Verify the project exists before deletion
      const verifyResponse = await fetch(deleteUrl, {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${strapiToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (verifyResponse.status === 404) {
        throw new Error('Project not found - cannot delete');
      }
      
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${strapiToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const responseText = await response.text();
        throw new Error(`Delete failed: ${response.status} - ${responseText}`);
      }
      
      toast.success('Project deleted successfully');
      await fetchPortfolios();
      
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error(`Failed to delete project: ${error.message}`);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    }
  }, [projectToDelete, getStrapiToken, fetchPortfolios]);

  // Event handlers
  const handleUploadSuccess = useCallback(() => {
    fetchPortfolios();
  }, [fetchPortfolios]);

  const handleCreateAlbum = useCallback(() => {
    setEditingProject(null);
    setUploadModalOpen(true);
  }, []);

  const handleEditProject = useCallback((project) => {
    setEditingProject(null); // Clear first to ensure useEffect triggers
    setTimeout(() => {
      setEditingProject(project); // Set the project data
      setUploadModalOpen(true);
    }, 0);
  }, []);

  const handleDeleteProject = useCallback((project) => {
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setUploadModalOpen(false);
    setEditingProject(null);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
  }, []);

  // Load portfolios on mount
  useEffect(() => {
    fetchPortfolios();
  }, [fetchPortfolios]);

  return {
    // State
    portfolios,
    loading,
    uploadModalOpen,
    editingProject,
    deleteDialogOpen,
    projectToDelete,
    deleting,
    
    // Functions
    getOrderedImages,
    handleUploadSuccess,
    handleCreateAlbum,
    handleEditProject,
    handleDeleteProject,
    handleCloseModal,
    handleCloseDeleteDialog,
    confirmDelete,
    fetchPortfolios
  };
};
